// Generic error / access-denied state for pages that fail to load or
// gate the user. Distinct from EmptyState (which is "no data yet") —
// this is for "something is wrong" or "you don't have permission."
//
// Usage:
//   PageBody: {
//     ErrorState: {
//       kind: 'forbidden',          // 'forbidden' | 'notFound' | 'serverError' | 'offline'
//       title: 'You don't have access',
//       hint: 'Ask an admin to grant the `admin.pages.read` permission.',
//       action: { text: 'Back to home', onClick: ... }
//     }
//   }
//
// `kind` selects the icon + default copy; `title` / `hint` / `action`
// override individual fields.

const _kindDefaults = {
  forbidden: {
    icon: 'lock',
    title: 'You don\'t have access',
    hint: 'Ask an admin to grant the right permission.'
  },
  notFound: {
    icon: 'search',
    title: 'Not found',
    hint: 'The page you\'re looking for doesn\'t exist.'
  },
  serverError: {
    icon: 'warning',
    title: 'Something went wrong',
    hint: 'A server error broke the page. Try again in a moment.'
  },
  offline: {
    icon: 'wifi',
    title: 'You\'re offline',
    hint: 'Reconnect to the internet to load this page.'
  },
  generic: {
    icon: 'warning',
    title: 'Couldn\'t load',
    hint: 'Refresh the page to try again.'
  }
}

const _resolve = (el, key) => {
  const defaults = _kindDefaults[el.kind || 'generic'] || _kindDefaults.generic
  return el[key] != null ? el[key] : defaults[key]
}

export const ErrorState = {
  flow: 'y',
  align: 'center center',
  gap: 'A',
  padding: 'C A',
  width: '100%',
  textAlign: 'center',
  // Live region so screen readers announce the error when the page
  // swaps in. role=alert for high priority.
  attr: { role: 'alert', 'aria-live': 'assertive' },

  // Default props — consumers override.
  kind: 'generic',
  title: null,
  hint: null,
  action: null,

  IconWrap: {
    flow: 'y',
    align: 'center center',
    width: 'C',
    height: 'C',
    round: '50%',
    background: 'overlay',
    color: 'red',
    Icon: {
      icon: (el) => _resolve(el.parent.parent, 'icon'),
      boxSize: 'A1'
    }
  },

  Title: {
    tag: 'h2',
    fontSize: 'A2',
    fontWeight: '700',
    color: 'title',
    margin: '0',
    text: (el) => _resolve(el.parent, 'title')
  },

  Hint: {
    tag: 'p',
    fontSize: 'Z2',
    color: 'caption',
    margin: '0',
    maxWidth: 'J',
    text: (el) => _resolve(el.parent, 'hint')
  },

  ActionBtn: {
    if: (el) => !!el.parent?.action,
    tag: 'button',
    type: 'button',
    padding: 'V A',
    round: 'Z',
    background: 'blue',
    color: 'white',
    fontSize: 'Z2',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    marginTop: 'V',
    text: (el) => el.parent?.action?.text || '',
    onClick: (e, el, s) => {
      const action = el.parent?.action
      if (typeof action?.onClick === 'function') action.onClick(e, el, s)
    }
  }
}
